/**
 * The free text beside an "Other" chip (LG-1030).
 *
 * A chip group that offers Other has nowhere to say what "other" means, so a
 * question declaring `other` stores that text beside its own answer, under
 * {@link otherAnswerName}. It is a companion of the chip answer, not a
 * question of its own: it is never drawn as its own row or read back as its
 * own line, and it means nothing once Other is not picked — which is why
 * {@link settleOtherAnswers} clears it, on the page and on the server alike.
 *
 * A leaf like `answer-shape.ts`: zod and types only, so the step schema, both
 * halves of the widget registry and the admin read path can all reach it
 * without closing an import cycle.
 */
import { z } from 'zod'
import type {
  AuditionAnswer,
  AuditionField,
  AuditionQuestionStep,
} from './steps.js'
import type { StoredSchema } from './widgets.js'

/** The chip label that opens the free text. */
export const OTHER_OPTION = 'Other'

/** A sentence, not an essay: this names a choice rather than arguing one. */
export const OTHER_TEXT_MAX_LENGTH = 200

/** Where a question's Other text is stored, beside its own answer. */
export function otherAnswerName(field: AuditionField): string {
  return otherKey(field.name)
}

function otherKey(name: string): string {
  return `${name}Other`
}

/** One name a question's answers may sit under, and its Other text beside it. */
export interface StoredName {
  readonly name: string
  readonly other?: string
}

/**
 * Every name one question's answers may be stored under: its own name and
 * each of its `formerNames`, with the Other text beside each where the
 * question offers Other — the text was written beside whichever name held
 * the answer. This is the whole namespace a read-back may ask for, and what
 * the name-uniqueness test holds distinct.
 */
export function storedNamesOf(field: AuditionField): StoredName[] {
  return [field.name, ...(field.formerNames ?? [])].map((name) =>
    field.other ? { name, other: otherKey(name) } : { name },
  )
}

/** Whether a chip answer, single or multi, has Other picked. */
export function picksOther(value: AuditionAnswer | undefined): boolean {
  return Array.isArray(value)
    ? value.includes(OTHER_OPTION)
    : value === OTHER_OPTION
}

/**
 * How the free text validates: trimmed, capped, and '' when it was never
 * sent, so the key is always present the way every other answer's is.
 */
export function otherTextSchema(): StoredSchema {
  return z
    .string()
    .trim()
    .max(OTHER_TEXT_MAX_LENGTH)
    .optional()
    .transform((v) => v ?? '')
}

/**
 * Clears every Other text whose chip is no longer picked.
 *
 * A stored "other" answer beside a chip that says something else is a data
 * trap: the admin page would print a reason for a choice nobody made. Run by
 * the step form after every edit and by `stepSchema` on the way in, so the
 * two can never keep different rules. Hands back the same bag when there is
 * nothing to clear, because the form runs it on every keystroke.
 */
export function settleOtherAnswers(
  step: AuditionQuestionStep,
  answers: Record<string, AuditionAnswer>,
): Record<string, AuditionAnswer> {
  let next = answers
  for (const field of step.fields) {
    if (!field.other || picksOther(next[field.name])) continue
    const name = otherAnswerName(field)
    if (next[name] === undefined || next[name] === '') continue
    next = { ...next, [name]: '' }
  }
  return next
}

/**
 * A chip answer's display parts, with the typed text written into its Other.
 *
 * Only the Other part changes, so text left beside a different pick — a row
 * written by hand, or before the schema cleared it — never reaches a reader.
 */
export function withOtherText(
  parts: string[],
  text: AuditionAnswer | undefined,
): string[] {
  if (typeof text !== 'string' || text === '') return parts
  return parts.map((part) =>
    part === OTHER_OPTION ? `${OTHER_OPTION}: ${text}` : part,
  )
}
